import { BarItemProps, Bar } from '@nivo/bar'
import dayjs from 'dayjs'
import { useContext, useEffect, useState } from 'react'
import { ReportingContext } from '../../../../contexts/reporting-context'
import { SeatBooking } from '../../../../types/andTypes'
import { useTooltip } from '@nivo/tooltip'
import { ToolTip, ChartBlock, ChartLabel } from '../reporting-charts-styles'
import useElementSize from '../../../../utilities/useElementSize'
import { AxisTickProps } from '@nivo/axes'
const weekDaysSorter: Record<string, number> = {
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5
}

type ChartData = {
  id: string
  label: string
  value: number
}
const LeftAlignedAxisText = (props: AxisTickProps<string>) => {
  return (
    <g>
      <text
        x={props.x - 90}
        y={props.y}
        textAnchor="start"
        dominantBaseline="central"
        fill="black"
        fontFamily="Poppins"
        fontSize={12}
      >
        {props.value}
      </text>
    </g>
  )
}
export const DeskBookingByDays = () => {
  const { reportSeatBookingsByTeam } = useContext(ReportingContext)
  const [bookingData, setBookingData] = useState<ChartData[]>([])
  const [chartRef, { width: chartWidth }] = useElementSize()

  useEffect(() => {
    constructBookingByDays(reportSeatBookingsByTeam)
  }, [reportSeatBookingsByTeam])

  const constructBookingByDays = (reportSeatBookings: SeatBooking[]) => {
    const daySeatsMap: Record<string, number> = {
      Monday: 0,
      Tuesday: 0,
      Wednesday: 0,
      Thursday: 0,
      Friday: 0
    }
    reportSeatBookings.forEach(data => {
      const day = dayjs(data.dateBooked).format('dddd')
      const seats = data.seats.length
      daySeatsMap[day] = daySeatsMap[day] !== undefined ? daySeatsMap[day] + seats : seats
    })
    const chartData: ChartData[] = []

    Object.keys(daySeatsMap).forEach(day => {
      chartData.push({
        id: day,
        label: day,
        value: daySeatsMap[day]
      })
    })

    setBookingData(chartData)
  }

  const BarComponent = ({ bar }: BarItemProps<{ id: string; label: string; value: number }>) => {
    const { showTooltipFromEvent, hideTooltip } = useTooltip()
    if (bar.data.value === 0) return null
    return (
      <g
        data-testid={`bar-chart-${bar.data.indexValue}`}
        transform={`translate(${bar?.x || 0},${bar?.y || 0})`}
        onMouseMove={(event): void => {
          showTooltipFromEvent(
            <ToolTip>
              {bar.data.indexValue}: {bar.data.value}
            </ToolTip>,
            event
          )
        }}
        onMouseLeave={(): void => {
          hideTooltip()
        }}
      >
        <rect
          width={bar.width}
          height={bar.height}
          fill={bar.color}
          data-testid={`bar-chart-${bar.data.indexValue}-${bar.data.value}`}
        />
      </g>
    )
  }
  const getTickValues = (data: ChartData[]) => {
    const maxSeats = Math.max(...data.map(({ value }) => value))
    switch (maxSeats) {
      case 0:
        return []
      case 1:
        return [0, 1]
      case 2:
        return [0, 1, 2]
      case 3:
        return [0, 1, 2, 3]
      default:
        return 4
    }
  }

  return (
    <ChartBlock ref={chartRef}>
      <ChartLabel>Desk bookings by day of the week:</ChartLabel>
      <Bar
        data={bookingData.sort((obj1, obj2) => {
          return weekDaysSorter[obj2.id] - weekDaysSorter[obj1.id]
        })}
        margin={{ top: 15, right: 15, bottom: 30, left: 150 }}
        padding={0.3}
        layout="horizontal"
        enableLabel={true}
        labelTextColor="white"
        enableGridY={false}
        enableGridX={false}
        colors={['#D82036']}
        axisLeft={{
          renderTick: props => LeftAlignedAxisText(props)
        }}
        axisBottom={{
          tickValues: getTickValues(bookingData),
          tickSize: 0,
          tickPadding: 10,
          legend: ''
        }}
        theme={{
          fontSize: 12,
          fontFamily: 'Poppins',
          axis: {
            domain: {
              line: {
                stroke: 'lightgrey',
                strokeWidth: 1
              }
            }
          }
        }}
        barComponent={BarComponent}
        height={350}
        width={chartWidth >= 20 ? chartWidth - 20 : 0}
      />
    </ChartBlock>
  )
}
